import { useState, useEffect } from 'react'
import Modal from './ui/Modal'
import Button from './ui/Button'
import Badge from './ui/Badge'
import SourcingAgent, { type SourcedCandidate, type SourcingAgentStatus } from '../lib/sourcingAgent'
import { api } from '../lib/api'
import type { Profile } from '../types/api'

type SourcingAgentModalProps = {
  isOpen: boolean
  onClose: () => void
  jobId: string
  jobTitle: string
}

type CandidateWithProfile = SourcedCandidate & {
  profileEmail?: string
  profileLocation?: string
}

function parseFrontmatter(md: string) {
  const match = md.match(/^---\n([\s\S]*?)\n---/)
  if (!match) return {} as Record<string, string>
  const result: Record<string, string> = {}
  const lines = match[1].split('\n')
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const colon = line.indexOf(':')
    if (colon === -1) { i++; continue }
    const key = line.slice(0, colon).trim()
    const raw = line.slice(colon + 1).trim()
    if (raw === '') {
      const items: string[] = []
      let j = i + 1
      while (j < lines.length && /^\s+-\s/.test(lines[j])) {
        items.push(lines[j].replace(/^\s+-\s*/, '').trim().replace(/^"|"$/g, ''))
        j++
      }
      result[key] = items.join(', ')
      i = j
    } else {
      result[key] = raw.replace(/^"|"$/g, '')
      i++
    }
  }
  return result
}

export default function SourcingAgentModal({ isOpen, onClose, jobId, jobTitle }: SourcingAgentModalProps) {
  const [status, setStatus] = useState<SourcingAgentStatus>('idle')
  const [candidates, setCandidates] = useState<CandidateWithProfile[]>([])
  const [error, setError] = useState<string | null>(null)
  const [agent] = useState(() => new SourcingAgent({ jobId, maxCandidates: 10, minScore: 50 }))

  useEffect(() => {
    if (isOpen && status === 'idle') {
      runSourcing()
    }
  }, [isOpen])

  const runSourcing = async () => {
    setStatus('sourcing')
    setError(null)
    setCandidates([])

    try {
      const result = await agent.start()
      setStatus(result.status)

      if (result.status === 'complete') {
        // Fetch additional profile details for each sourced candidate
        const enriched = await Promise.all(
          result.sourced.map(async (c) => {
            try {
              const profileRes = await api.profiles.get(c.profileId)
              const frontmatter = parseFrontmatter((profileRes.data as Profile).profile)
              return {
                ...c,
                profileEmail: frontmatter.email,
                profileLocation: frontmatter.location
              }
            } catch {
              return c
            }
          })
        )
        setCandidates(enriched)
      } else if (result.status === 'error') {
        setError(result.error || 'Sourcing failed')
      }
    } catch (err) {
      setStatus('error')
      setError(err instanceof Error ? err.message : 'Unknown error')
    }
  }

  const getMatchColor = (strength: string) => {
    switch (strength) {
      case 'strong': return 'bg-green-100 text-green-800 border-green-300'
      case 'moderate': return 'bg-yellow-100 text-yellow-800 border-yellow-300'
      case 'weak': return 'bg-red-100 text-red-800 border-red-300'
      default: return 'bg-gray-100 text-gray-800 border-gray-300'
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`AI SOURCING: ${jobTitle}`}>
      <div className="space-y-4">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {status === 'sourcing' && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
            <p className="text-sm text-text-muted">AI is sourcing candidates...</p>
          </div>
        )}

        {status === 'scoring' && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
            <p className="text-sm text-text-muted">Scoring candidates against job requirements...</p>
          </div>
        )}

        {status === 'generating' && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
            <p className="text-sm text-text-muted">Generating personalized outreach...</p>
          </div>
        )}

        {status === 'complete' && candidates.length === 0 && (
          <div className="text-center py-8">
            <p className="text-sm text-text-muted">No suitable candidates found. Try lowering the score threshold.</p>
          </div>
        )}

        {status === 'complete' && candidates.length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-text-muted">Found {candidates.length} candidates</p>
              <Button variant="ghost" onClick={runSourcing} className="text-xs">
                REFRESH
              </Button>
            </div>

            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {candidates.map((candidate) => (
                <div
                  key={candidate.profileId}
                  className="border border-border-subtle bg-surface-low p-4 hover:border-primary transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-foreground">{candidate.name}</p>
                      {candidate.profileEmail && (
                        <p className="text-xs text-text-muted">{candidate.profileEmail}</p>
                      )}
                      {candidate.profileLocation && (
                        <p className="text-xs text-text-muted">{candidate.profileLocation}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-black text-primary">{candidate.score}%</p>
                      <Badge
                        variant="required"
                        className={getMatchColor(candidate.matchStrength)}
                      >
                        {candidate.matchStrength.toUpperCase()}
                      </Badge>
                    </div>
                  </div>

                  {candidate.reasons.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border-subtle">
                      <p className="text-xs text-text-muted mb-1">WHY THEY MATCH:</p>
                      <ul className="text-xs text-foreground space-y-1">
                        {candidate.reasons.slice(0, 3).map((reason, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-primary mt-0.5">✓</span>
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-3 flex gap-2">
                    <Button variant="primary" className="text-xs flex-1">
                      CONTACT
                    </Button>
                    <Button variant="ghost" className="text-xs flex-1">
                      VIEW PROFILE
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
